import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Navbar, CustomButton, LoadingSpinner } from '../components';
import { plansAPI, paymentsAPI } from '../services/api';

const AddonPaymentSuccessPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [captureDone, setCaptureDone] = useState(false);
    const [captureData, setCaptureData] = useState(null);
    const [invoiceData, setInvoiceData] = useState(null);
    const [isPopup, setIsPopup] = useState(false);

    const orderId = useMemo(() => searchParams.get('token') || '', [searchParams]);

    useEffect(() => {
        // Remove query params from URL to prevent re-running capture on refresh/back
        if (orderId && window?.history?.replaceState) {
            window.history.replaceState({}, '', '/payment/addon/success');
        }
    }, [orderId]);

    useEffect(() => {
        const runCapture = async () => {
            if (!orderId) {
                toast.error('Missing PayPal order token.');
                navigate('/chat', { replace: true });
                return;
            }

            // Retrieve the pending addon plan ID from storage
            const addonPlanId = localStorage.getItem('pending_addon_plan_id');

            if (!addonPlanId) {
                // If missing, we might have already processed it or session lost
                // Attempt recovery via invoice lookup
                try {
                    const lookup = await paymentsAPI.getInvoiceByPaypalOrder(orderId);
                    if (lookup?.data) {
                        setInvoiceData(lookup.data);
                        setCaptureDone(true);
                        setLoading(false);
                        return;
                    }
                } catch (_) { }

                toast.error('Payment session expired. Please contact support if symptoms persist.');
                navigate('/chat', { replace: true });
                return;
            }

            const processedKey = `addon_processed_${orderId}`;
            const processingKey = `addon_processing_${orderId}`;

            // If already processed in this browser session, avoid duplicate capture call
            if (sessionStorage.getItem(processedKey) === 'true') {
                const cached = sessionStorage.getItem('addon_capture_result');
                if (cached) {
                    try {
                        const parsed = JSON.parse(cached);
                        if (parsed?.order_id === orderId) {
                            setCaptureData(parsed);
                            setCaptureDone(true);
                        }
                    } catch (_) { }
                }
                setLoading(false);
                return;
            }

            if (sessionStorage.getItem(processingKey) === 'true') {
                setLoading(false);
                return;
            }
            sessionStorage.setItem(processingKey, 'true');

            try {
                const response = await plansAPI.captureAddonPayPalOrder({
                    order_id: orderId,
                    addon_plan_id: parseInt(addonPlanId, 10)
                });

                const payload = response?.data || response;
                const normalized = {
                    order_id: orderId,
                    addon_plan_id: parseInt(addonPlanId, 10),
                    invoice_id: payload?.invoice_id || null,
                    tokens: payload?.tokens || payload?.token_balance?.tokens_added || null,
                    user_name: payload?.user_name || null,
                    payment_amount_usd: payload?.payment_amount_usd || null
                };

                setCaptureData(normalized);
                setCaptureDone(true);
                sessionStorage.setItem('addon_capture_result', JSON.stringify(normalized));
                sessionStorage.setItem(processedKey, 'true');
                localStorage.removeItem('pending_addon_plan_id');

                // Fetch detailed invoice information
                try {
                    const invoiceResponse = await paymentsAPI.getInvoiceByPaypalOrder(orderId);
                    if (invoiceResponse?.data) {
                        setInvoiceData(invoiceResponse.data);
                    }
                } catch (invoiceError) {
                    console.warn('Failed to fetch invoice details:', invoiceError);
                }
            } catch (error) {
                console.error('Addon capture failed:', error);
                toast.error(error?.message || 'Payment verification failed.');
            } finally {
                sessionStorage.removeItem(processingKey);
                setLoading(false);
            }
        };

        runCapture();
    }, [navigate, orderId]);

    useEffect(() => {
        // Detect if opened in a popup
        const checkPopup = () => {
            const hasOpener = window.opener && window.opener !== window;
            const isPaypalPopup = window.name === 'PayPal';
            if (hasOpener || isPaypalPopup) {
                setIsPopup(true);
            }
        };
        checkPopup();
    }, []);

    useEffect(() => {
        if (!captureDone || !isPopup) return;

        try {
            if (window.opener) {
                window.opener.postMessage({
                    type: 'PAYPAL_SUCCESS',
                    data: captureData,
                    context: 'addon'
                }, '*');
            }

            console.log('AddonPaymentSuccess: Closing popup in 500ms');
            setTimeout(() => {
                window.close();
            }, 500);
        } catch (e) {
            console.error('Failed to close addon popup:', e);
        }
    }, [captureDone, isPopup, captureData]);



    if (loading) {
        return (
            <div className="create-avatar-page">
                <Navbar disabled={true} />
                <LoadingSpinner fullScreen text="Confirming your tokens..." />
            </div>
        );
    }

    return (
        <div className="create-avatar-page">
            <Navbar disabled={true} />
            <Container className="upload-container">
                <div className="upload-header">
                    <h1>{captureDone ? 'Tokens Added Successfully' : 'Payment Verification'}</h1>
                </div>
                <div className="upload-form-wrapper">
                    <div className="form-section">
                        <p>
                            {captureDone
                                ? 'Your addon purchase has been verified and tokens added to your account.'
                                : 'Verification incomplete. Please check your account balance in a few minutes.'}
                        </p>
                        <div style={{ marginTop: '20px', borderTop: '1px solid var(--border-subtle)', paddingTop: '20px' }}>
                            <p><strong>Order ID:</strong> {orderId}</p>
                            {(captureData?.tokens || invoiceData?.tokens) && (
                                <p><strong>Tokens Added:</strong> {captureData?.tokens || invoiceData?.tokens}</p>
                            )}
                            {invoiceData?.user_name && <p><strong>User:</strong> {invoiceData.user_name}</p>}
                            {invoiceData?.payment_amount_usd && <p><strong>Amount:</strong> ${invoiceData.payment_amount_usd} USD</p>}
                        </div>
                    </div>

                    <div className="modal-actions" style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'center' }}>
                        <CustomButton
                            type="button"
                            variant="secondary"
                            onClick={() => navigate('/chat', { replace: true })}
                        >
                            Back to Chat
                        </CustomButton>
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default AddonPaymentSuccessPage;
